/**
 * Every tab of the project detail page, in the order `Project` declares its panels.
 *
 * `Tabs` addresses a panel by its position in the flattened panel list, so the
 * `project-tabs` query parameter is an index and nothing else. Linking to that
 * number by hand breaks the moment a tab is inserted, which is why callers ask
 * for a tab by name through `projectTabPath` instead.
 *
 * The index counts the panels that are rendered, and three of these tabs are
 * dropped for a caller holding no rule for what they show — invoices,
 * measurements and orders — so `projectTabPath` is told which ones are missing
 * rather than assuming all twelve. `useHiddenProjectTabs` is the one answer to
 * that question.
 *
 * A new tab is registered here at the position it is declared in `project.tsx` —
 * `projectTabCyId` will not type-check until it is, and the position is what the
 * links resolve to.
 */
export const PROJECT_TAB_ORDER = [
	"general",
	"timeline",
	"invoices",
	"contacts",
	"documents",
	"resource-links",
	"gallery",
	"protocol",
	"measurements",
	"acknowledgement-definitions",
	"orders",
	"settings",
] as const;

export type ProjectTab = (typeof PROJECT_TAB_ORDER)[number];

/**
 * Build the path to a project with one of its tabs selected.
 *
 * `Tabs` counts the index over the panels the caller can actually see, so this counts it the same
 * way: the three tabs a rule can drop — `invoices`, `measurements`, `orders` — are removed from
 * the order before the position is taken. `invoices` sits third, ahead of everything but the
 * general and timeline tabs, which is why the list has to be passed rather than assumed.
 *
 * @param projectId - The project to open.
 * @param tab - The tab to select, by name.
 * @param hidden - The tabs the caller holds no rule for, from `useHiddenProjectTabs`; they are not
 *   rendered, so they do not count towards the index either.
 * @returns A path including the `project-tabs` index the named tab has for this caller.
 */
export function projectTabPath(projectId: string, tab: ProjectTab, hidden: ProjectTab[] = []) {
	const visible = PROJECT_TAB_ORDER.filter((name) => !hidden.includes(name));
	// A caller who cannot see the tab a link asks for lands on the first one they can, because the
	// alternative is `-1`, which `Tabs` would take as an index.
	const index = visible.indexOf(tab);
	return `/projects/${projectId}?project-tabs=${index === -1 ? 0 : index}`;
}

/**
 * The `data-cy` id of a project tab, so a panel and the registry above cannot
 * disagree about which tabs exist.
 *
 * @param tab - The tab to identify, by name.
 * @returns The tab's `data-cy` id.
 */
export function projectTabCyId(tab: ProjectTab): `${ProjectTab}-tab` {
	return `${tab}-tab`;
}
